/*
 * Copyright (c) Domotz Ltd. All rights reserved.
 */

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import intl from 'react-intl-universal';

import AppNavbar from '../InternetNavbar';
import AppFooter from '../../components/AppFooter';
import NotFound from "../../components/NotFound";
import Loading from "../../components/Loading";
import SignUpPromoSectionBlue from "../../components/SignUpPromoSectionBlue";
import { toCompactAmount } from "../../helpers/IspScoreHelper";
import { renderMetaTags } from "../../helpers/MetaTagsHelper";

import IspMainView from './IspMainView';
import IspSidebarSummary from "./IspSidebarSummary";
import { getCountryCode, getCountryName } from "../../internet/place/GeoCountries";
import ScoreIndicator from "../../components/ScoreIndicator";
import { linkForPlace, linkForIsp } from "../LinkHelper";
import PlaceCarousel from "../../internet/place/PlaceCarousel";

class IspView extends Component {

    constructor(props) {
        super(props);
        this.state = {
            topIspByScore: [],
            topIspByLove: [],
            topCitiesInCountry: [],
            recentOutages: [],
            dropKpiPoints: [],
            item: {},
            isLoading: true,
            isFound: false
        };

    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
        this.setState({ item: {}, isLoading: false, isFound: false });
    }

    componentDidMount() {

        const countryParam = this.props.match.params.country;
        const regionParam = this.props.match.params.region;
        const cityParam = this.props.match.params.city;
        const ispId = this.props.match.params.ispId;

        this.mainFetchSequence(countryParam, regionParam, cityParam, ispId);
    }

    componentWillReceiveProps(nextProps) {

        if (this.props.match && nextProps && nextProps.match && nextProps.match.params &&
            this.props.match.url !== nextProps.match.url) {

            const countryParam = nextProps.match.params.country;
            const regionParam = nextProps.match.params.region;
            const cityParam = nextProps.match.params.city;
            const ispId = nextProps.match.params.ispId;

            this.mainFetchSequence(countryParam, regionParam, cityParam, ispId);

        }
    }

    mainFetchSequence(countryParam, regionParam, cityParam, ispId) {

        this.setState({ isLoading: true, isFound: false });

        const countryCode = countryParam.length === 2 ? countryParam : getCountryCode(countryParam);
        if (!countryCode) {
            this.setState({ isLoading: false, isFound: false });
            return;
        }
        const url = '/rest/internet/isp/' + ispId + '/' + countryCode + '/' + (regionParam ? regionParam : 'all') +
            (cityParam ? '/' + cityParam : '');

        fetch(url)
            .then(response => {
                if (response.status === 404) {
                    // check if /me is good...: we want to allow ISP page if it's your current
                    // ISP, so returned by geoip, even if we don't have stats for it...
                    return fetch('/rest/internet/me')
                        .then(response => this.validateRestResponse(response));

                } else if (response.status !== 200) {
                    throw Error(response.statusText);
                }
                return response;
            })
            .then(response => response.json())
            .then(data => {

                if (data.currentIsp) { // failover workflow from /me lookup
                    if (data.currentIsp.id === ispId) {
                        this.setState({ item: data.currentIsp, isLoading: false, isFound: true });
                        ispId = data.currentIsp.id;
                    } else {
                        throw Error('isp not found');
                    }
                } else { // standard workflow from /isp lookup
                    this.setState({ item: data, isLoading: false, isFound: true });
                }
                return fetch('/rest/internet/outages/' +
                    countryCode + '/' + (regionParam ? regionParam : 'all') + '/' + ispId +
                    '?top=10' + (cityParam ? '&city=' + cityParam : ''));
            })
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                if (data.reports && data.reports.length > 0) {
                    this.setState({ recentOutages: data.reports });
                }
                return fetch('/rest/place/cities/' + countryCode + '?top=13')
            })
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                if (cityParam) {
                    const index = data.map(p => p.cityName).indexOf(cityParam);
                    if (index >= 0)
                        data.splice(index, 1);
                }

                this.setState({ topCitiesInCountry: data });
                if (typeof window.initSlickCarousel === "function") {
                    window.initSlickCarousel();
                }
            })
            .catch(e => this.catchRestError(e));

        fetch('/rest/internet/dropkpi/' + countryCode + '/' + (regionParam ? regionParam : 'all') + '/' + ispId)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                this.setState({ dropKpiPoints: data.points });
            })
            .catch(e => this.catchRestError(e));


        fetch('/rest/internet/isps/' + countryCode +
            (regionParam ? '/' + regionParam : '/all') +
            (cityParam ? '/' + cityParam : '') + '?top=5')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                this.setState({ topIspByScore: data });
            })
            .catch(e => this.catchRestError(e));

        fetch('/rest/internet/isps/' + countryCode + (regionParam ? '/' + regionParam : '/all') +
            (cityParam ? '/' + cityParam : '') + '?top=5&sort=sentiment')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                this.setState({ topIspByLove: data });
            })
            .catch(e => this.catchRestError(e));

        window.scrollTo(0, 0);
    }

    render() {
        const { item, recentOutages, isLoading, isFound, dropKpiPoints } = this.state;

        if (isLoading) {
            return <Loading />;
        }

        if (!isFound) {
            return <NotFound />;
        }

        const countryName = getCountryName(item.countryCode);
        const regionName = item.countryRegion;
        const cityName = item.countryCity;

        const where = cityName ? cityName : regionName ? regionName : countryName;
        const whereLink = linkForPlace(countryName, regionName ? regionName : 'all', cityName);

        const lastOutage = recentOutages && recentOutages.length > 0 ? recentOutages[0] : null;

        return (
            <div>
                {renderMetaTags(item.name + " in " + where + ": speed performance and info about outage, service down or problems",
                    item.name + " in " + where + ": speed performance, user sentiment and outage overview. Problems or internet down? Real-time service status.", item.ispCardImageUrl)}

                <AppNavbar />

                <main role="main">
                    <div className="container space-2">
                        <div className="row">
                            <div className="col-lg-4 mb-9 mb-lg-0">

                                <div className="card card-bordered shadow-sm p-5 mb-5">
                                    <div className="border-bottom text-center pb-5 mb-5">
                                        <IspSidebarSummary isp={item} lastOutage={lastOutage} />
                                    </div>

                                    <div className="border-bottom pb-5 mb-5">
                                        <h4 className="font-size-1 font-weight-semi-bold text-uppercase mb-3">{intl.get('isp_main_performance_in', { where: where })}</h4>

                                        {this.state.topIspByScore.map(ispInCountry => this.renderSidebarIspRank(ispInCountry, false))}
                                        <Link className="small font-size-1 mt-2"
                                            to={whereLink}
                                            role="button">
                                            <span>{intl.getHTML('isp_main_view_all', { where: where })}</span>
                                            <span className="link-icon ml-1">
                                                <span className="link-icon-inner">+</span>
                                            </span>
                                        </Link>
                                    </div>

                                    <div className="mb-5">
                                        <h4 className="font-size-1 font-weight-semi-bold text-uppercase mb-3">{intl.get('isp_main_loved_in', { where: where })}</h4>

                                        {this.state.topIspByLove.map(ispInCountry => this.renderSidebarIspRank(ispInCountry, true))}
                                        <Link className="small font-size-1 mt-2"
                                            to={whereLink}
                                            role="button">
                                            <span>{intl.getHTML('isp_main_view_all', { where: where })}</span>
                                            <span className="link-icon ml-1">
                                                <span className="link-icon-inner">+</span>
                                            </span>
                                        </Link>
                                    </div>
                                </div>
                            </div>


                            <div className="col-lg-8">

                                <div className="pl-lg-4">
                                    <nav aria-label="breadcrumb">
                                        <ol className="breadcrumb breadcrumb-no-gutter bg-transparent font-size-1">
                                            <li className="breadcrumb-item"><Link to={'/'}>Internet</Link></li>
                                            <li className="breadcrumb-item"><Link to={linkForPlace(countryName)}>{countryName}</Link></li>
                                            {regionName &&
                                                <li className="breadcrumb-item"><Link to={linkForPlace(countryName, regionName)}>{regionName}</Link></li>
                                            }
                                            {cityName &&
                                                <li className="breadcrumb-item"><Link to={linkForPlace(countryName, regionName ? regionName : 'all', cityName)}>{cityName}</Link></li>
                                            }
                                            <li className="breadcrumb-item active" aria-current="page">{item.name}</li>
                                        </ol>
                                    </nav>
                                </div>


                                <IspMainView isp={item} recentOutages={recentOutages} dropKpiChart={dropKpiPoints} />
                            </div>

                        </div>
                    </div>

                    <SignUpPromoSectionBlue />

                    <div className="bg-light overflow-hidden">
                        <div className="container space-2">
                            {this.renderBestCitiesSection(item, this.state.topCitiesInCountry)}
                        </div>
                    </div>
                </main>

                <AppFooter />
            </div>
        );
    }

    renderSidebarIspRank(isp, showLove) {
        let score = 0;
        let fullClass, halfClass, emptyClass = '';
        let text = '';

        if (showLove) {
            score = isp.ispSentiment.sentimentValue;
            fullClass = halfClass = "fas fa-heart";
            emptyClass = "far fa-heart";
            text = intl.get('isp_main_reviews', { reviews: isp.ispSentiment.totalRatings });
        } else {
            score = isp.ispScore.scoreValue;
            fullClass = "fas fa-star";
            halfClass = "fas fa-star-half-alt";
            emptyClass = "far fa-star";
            text = intl.get('isp_main_sources', { samples: toCompactAmount(isp.ispScore.samples) });
        }


        return (
            <div className="media align-items-center mb-3">
                <div className="avatar avatar-xs mr-3">
                    <img className="avatar-img img-fluid" src={isp.logoImageUrl} alt="ISP Logo" />
                </div>
                <div className="media-body">
                    <h4 className="font-size-1 mb-0">
                        <Link to={linkForIsp(isp)}>{isp.name}</Link>
                    </h4>

                    <div className="d-md-flex align-items-md-center">
                        <ScoreIndicator score={score}
                            fullClass={fullClass}
                            halfClass={halfClass}
                            emptyClass={emptyClass} />
                        <small className="text-muted ml-2">{text}</small>
                    </div>
                </div>
            </div>
        );
    }

    renderBestCitiesSection(isp, itemList) {
        if (itemList.length === 0)
            return <section />;

        return (
            <section>
                <div className="w-md-80 w-lg-50 text-center mx-md-auto mb-9">
                    <span className="btn btn-xs btn-soft-success btn-pill mb-2">{intl.get('explore_badge')}</span>
                    <h2 className="text-primary">{intl.getHTML('isp_main_compare', { isp: isp.name, place: getCountryName(isp.countryCode) })}</h2>
                    <p>{intl.get('isp_main_compare_subtitle')}</p>
                </div>

                <PlaceCarousel
                    items={itemList}
                    makeTitle={place => place.cityName}
                    makeLink={place => '/provider/' + isp.id + '/' + getCountryName(place.countryCode) + '/' +
                        (place.regionName ? place.regionName : 'all') + '/' + place.cityName} />
            </section>
        );
    }

}

export default IspView;